import React, { useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "../../context/I18nContext";

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function normalizeOptions(raw) {
  const options = Array.isArray(raw) ? raw : [];
  return options
    .map((opt, index) => {
      const id = String(opt?.id ?? index + 1);
      const text = String(opt?.text ?? "");
      return { id, text };
    })
    .filter((opt) => opt.id);
}

function parseTemplate(template) {
  const raw = String(template || "");
  const parts = [];
  const re = /\{\{\s*([^}]+?)\s*\}\}/g;
  let lastIndex = 0;
  let match;
  while ((match = re.exec(raw))) {
    if (match.index > lastIndex) {
      parts.push({ type: "text", value: raw.slice(lastIndex, match.index) });
    }
    parts.push({ type: "blank", id: String(match[1]).trim() });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < raw.length) {
    parts.push({ type: "text", value: raw.slice(lastIndex) });
  }
  return parts;
}

function normalizePairs(raw) {
  const items = Array.isArray(raw) ? raw : [];
  return items
    .map((it, index) => ({
      id: String(it?.id ?? index + 1),
      text: String(it?.text ?? ""),
      image_url: String(it?.image_url ?? ""),
    }))
    .filter((it) => it.id);
}

function normalizeWordItems(raw) {
  const items = Array.isArray(raw) ? raw : [];
  return items
    .map((it, index) => ({
      id: String(it?.id ?? index + 1),
      text: String(it?.text ?? ""),
    }))
    .filter((it) => it.id);
}

function normalizeHotspots(raw) {
  const items = Array.isArray(raw) ? raw : [];
  return items
    .map((it, index) => ({
      id: String(it?.id ?? index + 1),
      x: Number(it?.x ?? 10),
      y: Number(it?.y ?? 10),
      w: Number(it?.w ?? 20),
      h: Number(it?.h ?? 12),
      label: String(it?.label ?? ""),
    }))
    .filter((it) => it.id);
}

function normalizeLabels(raw) {
  const items = Array.isArray(raw) ? raw : [];
  return items
    .map((it, index) => ({
      id: String(it?.id ?? index + 1),
      text: String(it?.text ?? ""),
    }))
    .filter((it) => it.id);
}

function className(prefix, name) {
  return `${prefix}__${name}`;
}

function SelectWordsRunner({ prefix, config, value, onChange }) {
  const mode = String(config?.mode || "circle");
  const items = normalizeWordItems(config?.items);
  const selected = asArray(value?.selected_item_ids).map(String);

  const toggle = (id) => {
    const isOn = selected.includes(id);
    const next = isOn ? selected.filter((x) => x !== id) : [...selected, id];
    onChange({ selected_item_ids: next });
  };

  return (
    <div
      className={`${className(prefix, "runner")} ${className(prefix, "runner")}--paper`}
    >
      <div
        className={
          mode === "highlight"
            ? `${className(prefix, "wordBank")} ${className(prefix, "wordBank")}--highlight`
            : `${className(prefix, "wordBank")} ${className(prefix, "wordBank")}--circle`
        }
      >
        {items.map((it) => (
          <button
            key={it.id}
            type="button"
            className={
              selected.includes(it.id)
                ? `${className(prefix, "wordItem")} ${className(prefix, "wordItem")}--on`
                : className(prefix, "wordItem")
            }
            onClick={() => toggle(it.id)}
          >
            {it.text || it.id}
          </button>
        ))}
      </div>
    </div>
  );
}

function MatchPairsArrows({
  prefix,
  left,
  right,
  pairs,
  onChange,
  config,
  tFn,
}) {
  const containerRef = useRef(null);
  const leftRefs = useRef({});
  const rightRefs = useRef({});

  const [activeLeft, setActiveLeft] = useState(null);
  const [layoutTick, setLayoutTick] = useState(0);

  const allowManyToOne = Boolean(config?.behavior?.allow_many_to_one);

  useLayoutEffect(() => {
    const onResize = () => setLayoutTick((x) => x + 1);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const containerBox = containerRef.current?.getBoundingClientRect();
  const getAnchor = (el) => {
    if (!el || !containerBox) return null;
    const box = el.getBoundingClientRect();
    return {
      x: box.left - containerBox.left + box.width / 2,
      y: box.top - containerBox.top + box.height / 2,
    };
  };

  const lines = [];
  for (const [l, r] of Object.entries(pairs || {})) {
    if (!r) continue;
    const lEl = leftRefs.current[l];
    const rEl = rightRefs.current[r];
    const a = getAnchor(lEl);
    const b = getAnchor(rEl);
    if (!a || !b) continue;
    lines.push({ leftId: l, rightId: r, a, b });
  }

  const connect = (leftId, rightId) => {
    if (!leftId || !rightId) return;
    const next = { ...(pairs || {}) };

    if (!allowManyToOne) {
      for (const [l, r] of Object.entries(next)) {
        if (r === rightId && l !== leftId) delete next[l];
      }
    }

    next[leftId] = rightId;
    onChange({ pairs: next });
    setActiveLeft(null);
  };

  return (
    <div
      className={`${className(prefix, "runner")} ${className(prefix, "runner")}--paper`}
    >
      <div
        className={`${className(prefix, "match")} ${className(prefix, "match")}--arrows`}
        ref={containerRef}
      >
        <svg className={className(prefix, "arrows")} aria-hidden="true">
          <defs>
            <marker
              id={`${prefix}-arrowhead`}
              markerWidth="10"
              markerHeight="10"
              refX="9"
              refY="5"
              orient="auto"
            >
              <path d="M0,0 L10,5 L0,10 Z" fill="rgba(30, 100, 218, 0.85)" />
            </marker>
          </defs>
          {lines.map((ln) => (
            <line
              key={`${ln.leftId}-${ln.rightId}-${layoutTick}`}
              x1={ln.a.x}
              y1={ln.a.y}
              x2={ln.b.x}
              y2={ln.b.y}
              className={className(prefix, "arrowLine")}
              markerEnd={`url(#${prefix}-arrowhead)`}
            />
          ))}
        </svg>

        <div className={className(prefix, "matchCol")}>
          {left.map((item) => {
            const isActive = activeLeft === item.id;
            return (
              <button
                key={item.id}
                type="button"
                ref={(el) => {
                  if (el) leftRefs.current[item.id] = el;
                }}
                className={
                  isActive
                    ? `${className(prefix, "matchPick")} ${className(prefix, "matchPick")}--active`
                    : className(prefix, "matchPick")
                }
                aria-pressed={isActive}
                onClick={() => setActiveLeft(isActive ? null : item.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setActiveLeft(isActive ? null : item.id);
                  }
                }}
              >
                <span className={className(prefix, "badge")}>{item.id}</span>
                <span>{item.text}</span>
              </button>
            );
          })}
        </div>

        <div
          className={`${className(prefix, "matchCol")} ${className(prefix, "matchCol")}--right`}
        >
          {right.map((item) => (
            <button
              key={item.id}
              type="button"
              ref={(el) => {
                if (el) rightRefs.current[item.id] = el;
              }}
              className={`${className(prefix, "matchPick")} ${className(prefix, "matchPick")}--right`}
              onClick={() => {
                if (!activeLeft) return;
                connect(activeLeft, item.id);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  if (!activeLeft) return;
                  e.preventDefault();
                  connect(activeLeft, item.id);
                }
              }}
            >
              <span
                className={`${className(prefix, "badge")} ${className(prefix, "badge")}--right`}
              >
                {item.id}
              </span>
              {item.image_url ? (
                <img
                  className={className(prefix, "matchImg")}
                  src={item.image_url}
                  alt={item.text || item.id}
                />
              ) : null}
              <span>{item.text}</span>
            </button>
          ))}
        </div>
      </div>

      <div className={className(prefix, "note")}>
        {tFn("testsModule.runner.matchInstruction", {
          defaultValue:
            "Select a left item, then select its match on the right.",
        })}
      </div>
    </div>
  );
}

function ImageLabelRunner({ prefix, config, value, onChange, tFn }) {
  const imageUrl = String(config?.image_url || "").trim();
  const hotspots = normalizeHotspots(config?.hotspots);
  const labels = normalizeLabels(config?.labels);

  const placements =
    value?.placements && typeof value.placements === "object"
      ? value.placements
      : {};
  const [activeLabelId, setActiveLabelId] = useState(null);
  const [activeHotspotId, setActiveHotspotId] = useState(null);

  // Touch-first: pick label, tap hotspot.
  const place = (hotspotId, labelId) => {
    if (!hotspotId) return;
    const next = { ...(placements || {}) };
    if (!labelId) {
      delete next[hotspotId];
    } else {
      next[hotspotId] = labelId;
    }
    onChange({ placements: next });
  };

  return (
    <div
      className={`${className(prefix, "runner")} ${className(prefix, "runner")}--paper`}
    >
      {imageUrl ? (
        <div className={className(prefix, "diagram")}>
          <div
            className={className(prefix, "imageLabel")}
            style={{ position: "relative" }}
            aria-label={tFn("testsModule.runner.imageAlt", {
              defaultValue: "Image",
            })}
          >
            <img
              className={className(prefix, "diagramImg")}
              src={imageUrl}
              alt={tFn("testsModule.runner.imageAlt", {
                defaultValue: "Image",
              })}
            />

            {hotspots.map((hs) => {
              const assigned = placements[hs.id];
              const assignedLabel = labels.find((l) => l.id === assigned);
              const isActiveHotspot = activeHotspotId === hs.id;
              return (
                <button
                  key={hs.id}
                  type="button"
                  className={className(prefix, "hotspot")}
                  style={{
                    position: "absolute",
                    left: `${hs.x}%`,
                    top: `${hs.y}%`,
                    width: `${hs.w}%`,
                    height: `${hs.h}%`,
                  }}
                  onClick={() => {
                    if (activeLabelId) {
                      place(hs.id, activeLabelId);
                      setActiveLabelId(null);
                      setActiveHotspotId(null);
                      return;
                    }
                    setActiveHotspotId(isActiveHotspot ? null : hs.id);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Backspace" || e.key === "Delete") {
                      e.preventDefault();
                      place(hs.id, null);
                      setActiveHotspotId(null);
                      return;
                    }
                    if (e.key === "Enter" || e.key === " ") {
                      // Let native button click handle.
                      return;
                    }
                  }}
                  aria-pressed={isActiveHotspot}
                  aria-label={`${tFn("testsModule.runner.hotspotLabel", {
                    defaultValue: "Hotspot {{id}}",
                    id: hs.id,
                  })}${assignedLabel?.text ? `: ${assignedLabel.text}` : ""}`}
                >
                  <span className={className(prefix, "hotspotText")}>
                    {assignedLabel?.text || ""}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      {labels.length ? (
        <div className={className(prefix, "labelBank")}>
          {labels.map((lb) => {
            const active = activeLabelId === lb.id;
            return (
              <button
                key={lb.id}
                type="button"
                className={
                  active
                    ? `${className(prefix, "labelChip")} ${className(prefix, "labelChip")}--active`
                    : className(prefix, "labelChip")
                }
                aria-pressed={active}
                onClick={() => {
                  if (activeHotspotId) {
                    place(activeHotspotId, lb.id);
                    setActiveHotspotId(null);
                    setActiveLabelId(null);
                    return;
                  }
                  setActiveLabelId(active ? null : lb.id);
                }}
              >
                {lb.text || lb.id}
              </button>
            );
          })}
        </div>
      ) : null}

      <div className={className(prefix, "note")}>
        {tFn("testsModule.runner.imageLabelInstruction", {
          defaultValue:
            "Pick a label then tap a hotspot (or pick a hotspot then a label). To clear: focus a hotspot and press Delete.",
        })}
      </div>
    </div>
  );
}

function DragDropRunner({ prefix, config, value, onChange, tFn }) {
  const mode = String(config?.mode || "order");
  const items = Array.isArray(config?.items)
    ? config.items.map((it, idx) => ({
        id: String(it?.id ?? idx + 1),
        text: String(it?.text ?? ""),
      }))
    : [];
  const categories = Array.isArray(config?.categories)
    ? config.categories.map((c, idx) => ({
        id: String(c?.id ?? idx + 1),
        label: String(c?.label ?? c?.text ?? ""),
      }))
    : [];

  if (mode === "categorize") {
    const map =
      value?.category_by_item_id &&
      typeof value.category_by_item_id === "object"
        ? value.category_by_item_id
        : {};
    return (
      <div
        className={`${className(prefix, "runner")} ${className(prefix, "runner")}--paper`}
      >
        <div
          className={className(prefix, "categorize")}
          role="group"
          aria-label={tFn("testsModule.runner.categorizeAria", {
            defaultValue: "Categorize",
          })}
        >
          {items.map((it) => (
            <label key={it.id} className={className(prefix, "categorizeRow")}>
              <span className={className(prefix, "categorizeItem")}>
                {it.text || it.id}
              </span>
              <select
                value={String(map[it.id] ?? "")}
                onChange={(e) =>
                  onChange({
                    category_by_item_id: { ...map, [it.id]: e.target.value },
                  })
                }
              >
                <option value="">
                  {tFn("testsModule.runner.choose", { defaultValue: "Choose" })}
                </option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label || c.id}
                  </option>
                ))}
              </select>
            </label>
          ))}
        </div>
      </div>
    );
  }

  const ordered = asArray(value?.ordered_item_ids).map(String);
  const defaultOrder = items.map((it) => it.id);
  const actualOrder = ordered.length
    ? ordered.filter((id) => defaultOrder.includes(id))
    : defaultOrder;

  const move = (id, dir) => {
    const idx = actualOrder.indexOf(id);
    if (idx < 0) return;
    const next = actualOrder.slice();
    const swapIdx = idx + dir;
    if (swapIdx < 0 || swapIdx >= next.length) return;
    const tmp = next[idx];
    next[idx] = next[swapIdx];
    next[swapIdx] = tmp;
    onChange({ ordered_item_ids: next });
  };

  return (
    <div
      className={`${className(prefix, "runner")} ${className(prefix, "runner")}--paper`}
    >
      <div className={className(prefix, "orderList")}>
        {actualOrder.map((id, idx) => {
          const it = items.find((x) => String(x.id) === id) || { id, text: id };
          return (
            <div key={id} className={className(prefix, "orderRow")}>
              <span className={className(prefix, "badge")}>{idx + 1}</span>
              <span className={className(prefix, "orderText")}>
                {it.text || it.id}
              </span>
              <div className={className(prefix, "orderBtns")}>
                <button
                  type="button"
                  className="btn ghost"
                  onClick={() => move(id, -1)}
                  aria-label={tFn("testsModule.runner.moveUp", {
                    defaultValue: "Move up",
                  })}
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="btn ghost"
                  onClick={() => move(id, 1)}
                  aria-label={tFn("testsModule.runner.moveDown", {
                    defaultValue: "Move down",
                  })}
                >
                  ↓
                </button>
              </div>
            </div>
          );
        })}
      </div>
      <div className={className(prefix, "note")}>
        {tFn("testsModule.runner.orderInstruction", {
          defaultValue: "Order the items.",
        })}
      </div>
    </div>
  );
}

function DataInterpretationRunner({ prefix, config, value, onChange, tFn }) {
  const stem = String(config?.stem ?? "").trim();
  const imageUrl = String(config?.image_url ?? "").trim();
  const items = Array.isArray(config?.items) ? config.items : [];

  const answersById =
    value?.answers_by_item_id && typeof value.answers_by_item_id === "object"
      ? value.answers_by_item_id
      : {};

  return (
    <div
      className={`${className(prefix, "runner")} ${className(prefix, "runner")}--paper`}
    >
      {stem ? <div className={className(prefix, "note")}>{stem}</div> : null}
      {imageUrl ? (
        <div className={className(prefix, "diagram")}>
          <img
            className={className(prefix, "diagramImg")}
            src={imageUrl}
            alt={tFn("testsModule.runner.dataAlt", { defaultValue: "Data" })}
          />
        </div>
      ) : null}

      <div className={className(prefix, "dataItems")}>
        {items.map((it, idx) => {
          const itemId = String(it?.id ?? idx + 1);
          const childExercise = {
            id: itemId,
            exercise_type: String(
              it?.type || it?.exercise_type || "multiple_choice",
            ),
            prompt: String(it?.prompt || ""),
            points: Number(it?.points ?? 1),
            config:
              typeof it?.config === "object" && it?.config ? it.config : {},
          };
          return (
            <div
              key={itemId}
              className={className(prefix, "dataItem")}
              style={{ marginTop: 12 }}
            >
              <div className={className(prefix, "prompt")}>
                <span className={className(prefix, "badge")}>{idx + 1}</span>
                <span>{childExercise.prompt}</span>
              </div>
              <ExerciseRunner
                prefix={prefix}
                exercise={childExercise}
                value={answersById[itemId]}
                onChange={(next) =>
                  onChange({
                    answers_by_item_id: { ...answersById, [itemId]: next },
                  })
                }
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function exerciseHasAnswer(exercise, answerValue) {
  const type = exercise?.exercise_type;
  if (!type) return false;
  if (type === "stimulus") return true;

  if (type === "multiple_choice")
    return (
      Array.isArray(answerValue?.selected_option_ids) &&
      answerValue.selected_option_ids.length
    );
  if (type === "fill_blanks")
    return (
      answerValue?.values &&
      typeof answerValue.values === "object" &&
      Object.values(answerValue.values).some((v) => String(v || "").trim())
    );
  if (type === "match_pairs" || type === "connect_arrow")
    return (
      answerValue?.pairs &&
      typeof answerValue.pairs === "object" &&
      Object.values(answerValue.pairs).some((v) => String(v || "").trim())
    );
  if (type === "select_words")
    return (
      Array.isArray(answerValue?.selected_item_ids) &&
      answerValue.selected_item_ids.length
    );
  if (type === "true_false") return typeof answerValue?.value === "boolean";
  if (type === "short_text")
    return String(answerValue?.text || "").trim().length > 0;
  if (type === "long_text" || type === "task" || type === "essay")
    return String(answerValue?.text || "").trim().length > 0;
  if (type === "diagram_label")
    return (
      answerValue?.values &&
      typeof answerValue.values === "object" &&
      Object.values(answerValue.values).some((v) => String(v || "").trim())
    );
  if (type === "image_label")
    return (
      answerValue?.placements &&
      typeof answerValue.placements === "object" &&
      Object.values(answerValue.placements).some((v) => String(v || "").trim())
    );
  if (type === "drag_drop") {
    if (
      answerValue?.ordered_item_ids &&
      Array.isArray(answerValue.ordered_item_ids) &&
      answerValue.ordered_item_ids.length
    )
      return true;
    if (
      answerValue?.category_by_item_id &&
      typeof answerValue.category_by_item_id === "object" &&
      Object.values(answerValue.category_by_item_id).some((v) =>
        String(v || "").trim(),
      )
    )
      return true;
    return false;
  }
  if (type === "data_interpretation") {
    return (
      answerValue?.answers_by_item_id &&
      typeof answerValue.answers_by_item_id === "object" &&
      Object.values(answerValue.answers_by_item_id).some(
        (v) => v && typeof v === "object",
      )
    );
  }

  return false;
}

export default function ExerciseRunner({
  prefix,
  exercise,
  value,
  onChange,
  language,
}) {
  const { t } = useTranslation();
  const tFn = (key, options = {}) => {
    const defaultValue = options.defaultValue;
    return t(key, { defaultValue, ...options });
  };

  const type = exercise?.exercise_type;
  const config = exercise?.config || {};

  if (!type) return null;

  if (type === "stimulus") {
    // Passage-only block (no answer expected).
    return null;
  }

  if (type === "diagram_label") {
    const imageUrl = String(config.image_url || "").trim();
    const items = Array.isArray(config.items) ? config.items : [];
    const values =
      value?.values && typeof value.values === "object" ? value.values : {};

    return (
      <div
        className={`${className(prefix, "runner")} ${className(prefix, "runner")}--paper`}
      >
        {imageUrl ? (
          <div className={className(prefix, "diagram")}>
            <img
              className={className(prefix, "diagramImg")}
              src={imageUrl}
              alt={tFn("testsModule.placement.diagramAlt", {
                defaultValue: "Diagram",
              })}
            />
          </div>
        ) : null}

        {items.length ? (
          <div className={className(prefix, "diagramFields")}>
            {items.map((it, idx) => {
              const id = String(it?.id ?? idx + 1);
              const label = String(it?.label ?? it?.text ?? id);
              return (
                <label key={id} className={className(prefix, "diagramField")}>
                  <span className={className(prefix, "diagramLabel")}>
                    {label}
                  </span>
                  <input
                    className={`${className(prefix, "input")} ${className(prefix, "input")}--paper`}
                    value={String(values[id] ?? "")}
                    onChange={(e) =>
                      onChange({
                        values: {
                          ...values,
                          [id]: e.target.value,
                        },
                      })
                    }
                    placeholder={tFn("testsModule.runner.writeLabel", {
                      defaultValue: "Write the label",
                    })}
                  />
                </label>
              );
            })}
          </div>
        ) : (
          <div className={className(prefix, "note")}>
            {tFn("testsModule.runner.diagramLabelingNote", {
              defaultValue: "Diagram labeling question.",
            })}
          </div>
        )}
      </div>
    );
  }

  if (type === "image_label") {
    return (
      <ImageLabelRunner
        prefix={prefix}
        config={config}
        value={value}
        onChange={onChange}
        tFn={tFn}
      />
    );
  }

  if (type === "task" || type === "essay") {
    const rows = Number(config.rows ?? 8);
    const placeholder = String(
      config.placeholder ??
        tFn("testsModule.runner.writeAnswer", {
          defaultValue: "Write your answer here...",
        }),
    );
    return (
      <div
        className={`${className(prefix, "runner")} ${className(prefix, "runner")}--paper`}
      >
        <textarea
          className={`${className(prefix, "textarea")} ${className(prefix, "textarea")}--paper`}
          rows={Number.isNaN(rows) ? 8 : rows}
          value={String(value?.text ?? "")}
          placeholder={placeholder}
          onChange={(e) => onChange({ text: e.target.value })}
        />
      </div>
    );
  }

  if (type === "multiple_choice") {
    const allowMultiple = Boolean(config.allow_multiple);
    const ui = String(config.ui || "list");
    const options = normalizeOptions(config.options);
    const selected = asArray(value?.selected_option_ids).map(String);

    return (
      <div
        className={`${className(prefix, "runner")} ${className(prefix, "runner")}--paper`}
      >
        <div
          className={
            ui === "underline"
              ? `${className(prefix, "mcq")} ${className(prefix, "mcq")}--underline`
              : className(prefix, "mcq")
          }
        >
          {options.map((opt) => {
            const on = selected.includes(opt.id);
            return (
              <button
                key={opt.id}
                type="button"
                className={
                  on
                    ? `${className(prefix, "mcqOption")} ${className(prefix, "mcqOption")}--on`
                    : className(prefix, "mcqOption")
                }
                onClick={() => {
                  if (allowMultiple) {
                    const next = on
                      ? selected.filter((x) => x !== opt.id)
                      : [...selected, opt.id];
                    onChange({ selected_option_ids: next });
                  } else {
                    onChange({ selected_option_ids: [opt.id] });
                  }
                }}
                aria-pressed={on}
              >
                <span className={className(prefix, "badge")}>{opt.id}</span>
                <span>{opt.text || opt.id}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  if (type === "fill_blanks") {
    const template = String(config.template || "");
    const parts = parseTemplate(template);
    const values =
      value?.values && typeof value.values === "object" ? value.values : {};

    return (
      <div
        className={`${className(prefix, "runner")} ${className(prefix, "runner")}--paper`}
      >
        <div className={className(prefix, "fill")}>
          {parts.map((p, idx) => {
            if (p.type === "text") {
              return (
                <span
                  key={`${p.type}-${idx}`}
                  className={className(prefix, "fillText")}
                >
                  {p.value}
                </span>
              );
            }

            const blankId = String(p.id || idx + 1);
            return (
              <input
                key={`${p.type}-${blankId}-${idx}`}
                className={`${className(prefix, "blank")} ${className(prefix, "input")} ${className(prefix, "input")}--paper`}
                value={String(values[blankId] ?? "")}
                onChange={(e) =>
                  onChange({
                    values: {
                      ...values,
                      [blankId]: e.target.value,
                    },
                  })
                }
                aria-label={tFn("testsModule.runner.blankLabel", {
                  defaultValue: "Blank {{id}}",
                  id: blankId,
                })}
              />
            );
          })}
        </div>
      </div>
    );
  }

  if (type === "match_pairs" || type === "connect_arrow") {
    const left = normalizePairs(config.left);
    const right = normalizePairs(config.right);
    const ui = String(
      config.ui || (type === "connect_arrow" ? "arrows" : "select"),
    );

    const pairs =
      value?.pairs && typeof value.pairs === "object" ? value.pairs : {};

    if (ui === "arrows") {
      return (
        <MatchPairsArrows
          prefix={prefix}
          left={left}
          right={right}
          pairs={pairs}
          onChange={onChange}
          config={config}
          tFn={tFn}
        />
      );
    }

    return (
      <div
        className={`${className(prefix, "runner")} ${className(prefix, "runner")}--paper`}
      >
        <div className={className(prefix, "match")}>
          {left.map((l) => (
            <label key={l.id} className={className(prefix, "matchRow")}>
              <span className={className(prefix, "badge")}>{l.id}</span>
              <span className={className(prefix, "matchLeft")}>
                {l.text || l.id}
              </span>
              <select
                value={String(pairs[l.id] ?? "")}
                onChange={(e) =>
                  onChange({
                    pairs: {
                      ...pairs,
                      [l.id]: e.target.value,
                    },
                  })
                }
              >
                <option value="">
                  {tFn("testsModule.runner.choose", { defaultValue: "Choose" })}
                </option>
                {right.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.id} — {r.text || r.id}
                  </option>
                ))}
              </select>
            </label>
          ))}
        </div>
      </div>
    );
  }

  if (type === "true_false") {
    const current = value?.value;
    return (
      <div
        className={`${className(prefix, "runner")} ${className(prefix, "runner")}--paper`}
      >
        <div className={className(prefix, "tf")}>
          <button
            type="button"
            className={
              current === true
                ? `${className(prefix, "tfBtn")} ${className(prefix, "tfBtn")}--on`
                : className(prefix, "tfBtn")
            }
            onClick={() => onChange({ value: true })}
            aria-pressed={current === true}
          >
            {tFn("testsModule.runner.true", { defaultValue: "True" })}
          </button>
          <button
            type="button"
            className={
              current === false
                ? `${className(prefix, "tfBtn")} ${className(prefix, "tfBtn")}--on`
                : className(prefix, "tfBtn")
            }
            onClick={() => onChange({ value: false })}
            aria-pressed={current === false}
          >
            {tFn("testsModule.runner.false", { defaultValue: "False" })}
          </button>
        </div>
      </div>
    );
  }

  if (type === "select_words") {
    return (
      <SelectWordsRunner
        prefix={prefix}
        config={config}
        value={value}
        onChange={onChange}
      />
    );
  }

  if (type === "short_text") {
    const placeholder = String(config.placeholder ?? "");
    return (
      <div
        className={`${className(prefix, "runner")} ${className(prefix, "runner")}--paper`}
      >
        <input
          className={`${className(prefix, "input")} ${className(prefix, "input")}--paper`}
          value={String(value?.text ?? "")}
          onChange={(e) => onChange({ text: e.target.value })}
          placeholder={placeholder}
        />
      </div>
    );
  }

  if (type === "long_text") {
    const rows = Number(config.rows ?? 6);
    return (
      <div
        className={`${className(prefix, "runner")} ${className(prefix, "runner")}--paper`}
      >
        <textarea
          className={`${className(prefix, "textarea")} ${className(prefix, "textarea")}--paper`}
          rows={Number.isNaN(rows) ? 6 : rows}
          value={String(value?.text ?? "")}
          onChange={(e) => onChange({ text: e.target.value })}
        />
      </div>
    );
  }

  if (type === "drag_drop") {
    return (
      <DragDropRunner
        prefix={prefix}
        config={config}
        value={value}
        onChange={onChange}
        tFn={tFn}
      />
    );
  }

  if (type === "data_interpretation") {
    return (
      <DataInterpretationRunner
        prefix={prefix}
        config={config}
        value={value}
        onChange={onChange}
        tFn={tFn}
      />
    );
  }

  return (
    <div
      className={`${className(prefix, "note")} ${className(prefix, "note")}--warning`}
    >
      {tFn("testsModule.runner.unsupportedType", {
        defaultValue: "Unsupported question type: {{type}}",
        type,
      })}
    </div>
  );
}
